<script setup lang="ts">
  import type { CampaignBlockStoryblok, BigCampaignBlockItemStoryblok } from '@/storyblok/component-types-sb'
  import type { ISbStoryData } from 'storyblok'
  import type { SwiperSettings } from '@/components/ui/slider/types'
  import type Swiper from 'swiper'
  import useIndexPageDataLayer from '@/composables/data-layers/useIndexPageDataLayer'

  import {
    BIG_CAMPAIGN_BLOCK_ITEM_VARIANT,
    MULTI_CAMPAIGN_BLOCK_VARIANT,
    SMALL_CAMPAIGN_BLOCK_ITEM_VARIANT_WITH_IMAGE_ON_TOP_VARIANT
  } from '@/utils/constants/campaignBlock'
  import { hasStringValue } from '@/utils/stringManipulation'
  import '@/assets/css/swiper.css'
  import { COOKIE_OPTIONS } from '@/utils/constants/cookies'

  const countryCookie = useCookie<string | undefined>('country', { ...COOKIE_OPTIONS })

  const CAROUSEL_NEXT = 'carousel-next'
  const CAROUSEL_PREV = 'carousel-prev'
  const CAROUSEL_PAGINATION = 'carousel-pagination'

  const props = defineProps<{ blok: CampaignBlockStoryblok }>()

  const { isMobile } = useBTBreakpoints()

  const campaignBlockItems = computed(() => {
    return props.blok.campaignBlockItems as ISbStoryData<BigCampaignBlockItemStoryblok>[]
  })

  const campaignBlockItemComponent = computed(() => {
    if (props.blok.itemVariant === SMALL_CAMPAIGN_BLOCK_ITEM_VARIANT_WITH_IMAGE_ON_TOP_VARIANT)
      return 'SmallCampaignBlockItemWithImageOnTop'
    if (props.blok.itemVariant === BIG_CAMPAIGN_BLOCK_ITEM_VARIANT) return 'BigCampaignBlockItem'

    return 'SmallCampaignBlockItem'
  })

  const dayjs = useDayjs()

  const isVisibleInCountry = (item: ISbStoryData<BigCampaignBlockItemStoryblok>) => {
    const visibleInCountries = item?.content?.visibleInCountries

    if (!visibleInCountries || visibleInCountries.length === 0 || !countryCookie.value) return true

    return visibleInCountries.includes(countryCookie.value)
  }

  const isWithinBookAFlightWindow = (item: ISbStoryData<BigCampaignBlockItemStoryblok>) => {
    const fromRaw = item.content.bookAFlightFrom
    const toRaw = item.content.bookAFlightTo

    if (!hasStringValue(fromRaw) || !hasStringValue(toRaw)) return true

    const from = dayjs(fromRaw).startOf('day')
    const to = dayjs(toRaw).endOf('day')

    if (!from.isValid() || !to.isValid()) return true

    return dayjs().isBetween(from, to, 'hour', '[]')
  }

  const visibleBlockItems = computed(() => {
    return campaignBlockItems.value.filter((item) => {
      if (!item?.content) return false
      if (!isVisibleInCountry(item)) return false
      if (props.blok.itemVariant !== BIG_CAMPAIGN_BLOCK_ITEM_VARIANT) return true

      return isWithinBookAFlightWindow(item)
    })
  })

  const showMultipleItems = computed(() => props.blok.variant === MULTI_CAMPAIGN_BLOCK_VARIANT && !isMobile.value)

  const shouldAutoplay = computed(() => props.blok.itemVariant === BIG_CAMPAIGN_BLOCK_ITEM_VARIANT)

  const autoplayConfig = {
    delay: 4000,
    disableOnInteraction: false,
    pauseOnMouseEnter: true,
    waitForTransition: true,
    reverseDirection: false
  }

  const sliderSettings = computed<SwiperSettings>(() => ({
    speed: 500,
    keyboard: true,
    slidesPerView: showMultipleItems.value ? 2 : 1,
    navigation: {
      nextEl: `#${CAROUSEL_NEXT}-${props.blok._uid}`,
      prevEl: `#${CAROUSEL_PREV}-${props.blok._uid}`
    },
    spaceBetween: props.blok.variant === MULTI_CAMPAIGN_BLOCK_VARIANT ? -16 : 0,
    loop: false,
    watchSlidesProgress: true,
    slidesPerGroup: showMultipleItems.value ? 2 : 1,
    pagination: isMobile.value
      ? false
      : {
          el: `#${CAROUSEL_PAGINATION}-${props.blok._uid}`,
          clickable: true,
          bulletActiveClass: 'border-bt-blue-dark rounded-full',
          renderBullet: (index: number, className: string) => {
            return `<span class="${className} w-8 lg:w-14 border-b-4 border-border block h-1 cursor-pointer"></span>`
          }
        },
    autoHeight: false,
    autoplay: shouldAutoplay.value ? autoplayConfig : false
  }))

  const activeIndex = ref(0)
  function onActiveSlideIndexChange(event: CustomEvent<Swiper[]>) {
    activeIndex.value = event.detail[0].realIndex
  }

  const campaignBlockWrapper = ref<HTMLElement | null>(null)
  const isVisibleHalf = useElementVisibility(campaignBlockWrapper, { threshold: 0.5 })
  const hasSentImpression = ref(false)

  const { pushIndexPageDataLayer } = useIndexPageDataLayer()
  const genericEventName = computed(() => {
    if (props.blok.variant === 'single') {
      return 'campaign_banner_single_block'
    }

    if (props.blok.variant === 'multiple') {
      return 'campaign_banner_multiple_block'
    }

    return undefined
  })

  watch(isVisibleHalf, (visible) => {
    if (hasSentImpression.value) return
    if (!visible) return

    pushIndexPageDataLayer({
      generic_event_name: genericEventName.value,
      event_category: 'impressions',
      event_label: visibleBlockItems.value[activeIndex.value]?.name ?? visibleBlockItems.value[0]?.name
    })

    hasSentImpression.value = true
  })

  const { t } = useI18n()
</script>

<template>
  <div
    v-if="visibleBlockItems.length && sliderSettings"
    ref="campaignBlockWrapper"
    class="relative -mx-5 mb-4 md:mb-10 xl:mb-14"
  >
    <LazyUiSliderComponent
      :slides="visibleBlockItems"
      :slider-settings="sliderSettings"
      @swiperactiveindexchange="onActiveSlideIndexChange"
    >
      <template #slide="{ slide }">
        <Component
          :is="campaignBlockItemComponent"
          v-if="typeof slide !== 'string' && slide.content"
          :blok="slide.content"
          :variant="blok.itemVariant"
        />
      </template>

      <template #slider-container-end>
        <div
          v-if="!isMobile"
          :id="`${CAROUSEL_PAGINATION}-${blok._uid}`"
          class="flex justify-center gap-4 sm:mt-8 lg:mt-12 xl:mt-10"
        />
        <div
          v-else
          class="relatie flex items-center justify-center sm:mt-8"
        >
          <span class="absolute max-w-50">{{
            t('pagination.of', { page: activeIndex + 1, totalPages: visibleBlockItems.length })
          }}</span>
          <UiSliderNavigationRoundArrows
            class="flex w-full justify-between px-5"
            :prev-btn-id="`${CAROUSEL_PREV}-${blok._uid}`"
            :next-btn-id="`${CAROUSEL_NEXT}-${blok._uid}`"
          />
        </div>
      </template>

      <template #slider-outside>
        <UiSliderNavigationRoundArrows
          class="absolute -left-12 top-[calc(50%-2.5rem)] hidden w-[calc(100%+6rem)] justify-between min-[1280px]:flex min-[1416px]:hidden min-[1552px]:flex"
          :prev-btn-id="`${CAROUSEL_PREV}-${blok._uid}`"
          :next-btn-id="`${CAROUSEL_NEXT}-${blok._uid}`"
        />
      </template>
    </LazyUiSliderComponent>
  </div>
</template>
